<script lang="ts">
  import { removeSessionStats, type SessionStats, SessionType, setSessionStats } from "@/js/settings.ts";
  import { roundValue } from "@/js/utils.ts";
  import { Block, Button, f7, Link, List, ListInput, ListItem, PageContent, Range, Sheet, Toolbar } from "framework7-svelte";
  import { type Calendar } from "framework7/lite/bundle";
  import { onDestroy, onMount } from "svelte";
  import { formatDuration } from "../../js/date";
  import { getLanguage, translate as tr } from "../../js/i18n";
  import { getSessionsForDay, getStats, type Stats } from "../../js/session.ts";

  let stats: Stats = getStats();
  let curDaySessionsRef: any[] = []; // eslint-disable-line
  let language = getLanguage();
  let selectedDate = new Date();
  let calendarInline: Calendar.Calendar;
  const allSessionDates: Date[] = [];

  let sheetOpened = false;
  let curEditStat: SessionStats | null;
  let curEditStatDate: string;
  let curEditElapsed: number;

  function updateDaysSessions(date: Date) {
    curDaySessionsRef = getSessionsForDay(stats, date.getFullYear(), date.getMonth(), date.getDate());
  }

  const processSessions = (): void => {
    const daySet = new Set<string>();
    stats.sessions.forEach((session) => {
      if (!session.date) return;
      const dt = new Date(session.date);
      const dayStr = `${dt.getFullYear()}-${dt.getMonth()}-${dt.getDate()}`;
      if (daySet.has(dayStr)) return;
      daySet.add(dayStr);
      allSessionDates.push(new Date(dt.getFullYear(), dt.getMonth(), dt.getDate()));
    });
  };

  const onDeletePressed = () => {
    if (!curEditStat) return;
    const dt = new Date(curEditStat.date);
    removeSessionStats(curEditStat.date);
    stats = getStats(); // reset stats
    updateDaysSessions(dt);
  };

  const dateToInputString = (date: Date): string => {
    var fDate = new Date(date);
    fDate.setMinutes(fDate.getMinutes() - fDate.getTimezoneOffset());
    return fDate.toISOString().slice(0, 16); // date and time
  };

  const addNewSessionPressed = () => {
    curEditStat = null;
    curEditElapsed = 60 * 5; // default to 5 min
    curEditStatDate = dateToInputString(new Date());
    sheetOpened = true;
  };

  const onSavePressed = () => {
    const isNewStat = curEditStat === null;
    if (curEditStat === null) {
      curEditStat = {
        date: curEditStatDate ? new Date(curEditStatDate).getTime() : Date.now(),
        elapsed: curEditElapsed,
        type: SessionType.coherence
      };
      // add new stat
      stats.sessions.push(curEditStat);
      console.log("stats: creating new statistic", curEditStat);
    }
    curEditStat.elapsed = curEditElapsed; // update that stat
    setSessionStats(stats.sessions); // save updates
    stats = getStats(); // reset stats to trigger reactivity
    if (isNewStat) {
      // no inline calendar on overview page
    }
    const dt = new Date(curEditStat.date);
    updateDaysSessions(dt);
  };

  const onDaySelected = (stat: SessionStats): void => {
    if (!stat) return;
    curEditStat = stat;
    curEditElapsed = stat.elapsed;
    curEditStatDate = dateToInputString(new Date(stat.date));
    sheetOpened = true;
  };

  const showCalendar = () => {
    processSessions();
    const monthNames = [...Array(12).keys()].map((key) => new Date(0, key).toLocaleDateString(language, { month: "long" }));
    calendarInline = f7.calendar.create({
      containerEl: "#calendar-inline-container",
      multiple: true,
      value: allSessionDates,
      disabled: (date: Date) => {
        const daySessStats = getSessionsForDay(stats, date.getFullYear(), date.getMonth(), date.getDate());
        return daySessStats.length === 0;
      },
      renderToolbar: () =>
        '<div class="toolbar calendar-custom-toolbar no-shadow" style="background-color: unset;">' +
        '<div class="toolbar-inner">' +
        '<div class="left">' +
        '<a class="link icon-only"><i class="icon material-icons">arrow_left</i></a>' +
        "</div>" +
        '<div class="center"></div>' +
        '<div class="right">' +
        '<a class="link icon-only"><i class="icon material-icons">arrow_right</i></a>' +
        "</div>" +
        "</div>" +
        "</div>",
      on: {
        dayClick: (_calendar: Calendar.Calendar, _dayEl: HTMLElement, year: number, month: number, day: number) => {
          updateDaysSessions(new Date(year, month, day));
          calendarInline.setValue(allSessionDates);
        },
        init: (calendar: Calendar.Calendar) => {
          f7.$(".calendar-custom-toolbar .center").text(monthNames[calendar.currentMonth] + ", " + calendar.currentYear);
          f7.$(".calendar-custom-toolbar .left .link").on("click", () => calendarInline.prevMonth(0));
          f7.$(".calendar-custom-toolbar .right .link").on("click", () => calendarInline.nextMonth(0));
          const showDt = selectedDate;
          updateDaysSessions(showDt);
        },
        monthYearChangeStart: (calendar: Calendar.Calendar) => {
          f7.$(".calendar-custom-toolbar .center").text(monthNames[calendar.currentMonth] + ", " + calendar.currentYear);
        }
      }
    });
  };

  onMount(() => showCalendar());

  onDestroy(() => {
    calendarInline.destroy();
  });
</script>

<div class="page-stats-calendar">
  <Block>
    <div id="calendar-inline-container"></div>
    <div class="display-flex justify-content-space-between align-items-center" style="margin-right: 8px; font-size: 0.9em;">
      <div>🟣 {tr("stats.day_with_coherence")}</div>
      <div>
        <Link iconMaterial="add_circle_outline" iconOnly={true} href="#" title={tr("settings.my_journal")} onClick={addNewSessionPressed}
          >{tr("common.add")}</Link>
      </div>
    </div>
  </Block>

  {#if curDaySessionsRef.length > 0}
    <List mediaList dividersIos outlineIos strongIos>
      <ListItem
        title={`📅 ${new Date(curDaySessionsRef[0].date).toLocaleDateString(language, {
          day: "numeric",
          month: "long",
          weekday: "long"
        })}`}
        after={`${curDaySessionsRef.length} ${tr("common.session", { count: curDaySessionsRef.length })}`}></ListItem>
      {#each curDaySessionsRef as stat}
        <ListItem
          link="#"
          onClick={() => onDaySelected(stat)}
          title={`⏱ ${stat.date ? new Date(stat.date).toLocaleTimeString(language) : "Unknown"}`}
          after={stat.elapsed ? formatDuration(stat.elapsed * 1000) : "0"}>
        </ListItem>
      {/each}
    </List>
  {/if}

  <Sheet class="sheet-edit" opened={sheetOpened} onSheetClosed={() => (sheetOpened = false)}>
    <Toolbar>
      <Button
        sheetClose
        iconMaterial="delete"
        color="red"
        onClick={onDeletePressed}
        text={tr("common.delete")}
        disabled={curEditStat == null} />
      <Button sheetClose iconMaterial="save" color="primary" onClick={onSavePressed} text={tr("common.save")} />
      <Button sheetClose iconMaterial="cancel" text={tr("common.cancel")} />
    </Toolbar>
    <PageContent>
      <Block>
        <List>
          {#if !curEditStat?.date}
            <ListInput label={tr("common.date")} type="datetime-local" bind:value={curEditStatDate}></ListInput>
          {/if}
          <ListInput
            label={`${roundValue(curEditElapsed / 60)} ${tr("common.minute", { count: roundValue(curEditElapsed / 60) })}`}
            input={false}>
            <div slot="input">
              <Range
                bind:value={curEditElapsed}
                min={1}
                max={3600}
                step={1}
                label={true}
                formatLabel={(value) => `${roundValue(value / 60)}`} />
            </div>
          </ListInput>
        </List>
      </Block>
    </PageContent>
  </Sheet>
</div>

<style>
  :root {
    --f7-calendar-selected-bg-color: violet;
  }
</style>
